import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ApiService from '../services/api';
import PushNotificationService from '../services/pushNotifications';
import LocalNotificationService from '../services/localNotifications';
import { User, LoginRequest, RegisterRequest } from '../types';
import { storage } from '../utils/storage';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPendingRegistration: boolean;
  showAddressModal: boolean;
  pendingUserData: any;
  pendingToken: string;
  login: (credentials: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  register: (userData: RegisterRequest) => Promise<{ success: boolean; error?: string; user?: any; token?: string; skeletonMerge?: any }>;
  completeRegistration: (userData: any, token: string) => Promise<void>;
  setShowAddressModal: (show: boolean) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPendingRegistration, setIsPendingRegistration] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [pendingUserData, setPendingUserData] = useState<any>(null);
  const [pendingToken, setPendingToken] = useState<string>('');
  const apiService = ApiService.getInstance();
  const pushNotificationService = PushNotificationService.getInstance();
  const localNotificationService = LocalNotificationService.getInstance();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);

      // Wait for API service to initialize token from native storage
      await apiService.waitForTokenInit();

      // Check if we have a stored token (use async storage for native platforms)
      const token = await storage.getItem('authToken');

      if (!token) {
        setIsLoading(false);
        return;
      }

      // Ensure ApiService has the token set (important for native platforms)
      apiService.setToken(token);

      // Try to get user data from storage first
      const userData = await storage.getItem('userData');

      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (e) {
          console.error('Failed to parse stored user data:', e);
        }
      }

      // Get full profile data from the server
      const response = await apiService.getProfile();

      if (response.success && response.data) {
        // Preserve skeletonMerge data from storage if it exists
        // (profile endpoint doesn't return this, but it's important for onboarding)
        const existingUser = await storage.getItem('userData');
        let skeletonMergeData = null;
        if (existingUser) {
          try {
            const parsed = JSON.parse(existingUser);
            skeletonMergeData = parsed.skeletonMerge;
            console.log('🔍 AuthContext: Preserving skeletonMerge from storage:', skeletonMergeData);
          } catch (e) {
            // Ignore parse errors
          }
        }

        // Merge skeleton data back into the refreshed user
        const updatedUser = skeletonMergeData
          ? { ...response.data, skeletonMerge: skeletonMergeData }
          : response.data;

        console.log('🔍 AuthContext: Setting user after profile refresh:', updatedUser);
        setUser(updatedUser);
        await storage.setItem('userData', JSON.stringify(updatedUser));

        // Initialize push notifications after successful auth
        await pushNotificationService.initialize();
        // Initialize local notifications for activity reminders
        await localNotificationService.initialize();
        await localNotificationService.cleanupExpiredReminders();
      } else {
        // Token is invalid, clear it
        await storage.removeItem('authToken');
        await storage.removeItem('userData');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Clear potentially invalid tokens
      await storage.removeItem('authToken');
      await storage.removeItem('userData');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginRequest): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      
      const response = await apiService.login(credentials);

      if (response.success && response.data) {
        setUser(response.data.user);

        // Initialize push notifications after successful login
        await pushNotificationService.initialize();
        // Initialize local notifications for activity reminders
        await localNotificationService.initialize();
        await localNotificationService.cleanupExpiredReminders();

        return { success: true };
      } else {
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Login failed' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterRequest): Promise<{ success: boolean; error?: string; user?: any; token?: string; skeletonMerge?: any }> => {
    try {
      setIsLoading(true);

      const response = await apiService.register(userData);

      console.log('🔍 AuthContext: Full registration response:', response);

      if (response.success && response.data) {
        const { user, token, skeletonMerge } = response.data;

        console.log('🔍 AuthContext: Registration response data:', response.data);
        console.log('🔍 AuthContext: Registration response skeletonMerge:', skeletonMerge);

        // Set the token in API service immediately so address modal can make authenticated requests
        apiService.setToken(token);

        // Check if skeleton accounts were merged (indicating existing connections)
        if (skeletonMerge && skeletonMerge.hasConnectionsWaiting) {
          console.log('✅ AuthContext: Skeleton connections waiting, storing merge data');
          // Store merge info for later use
          const userWithSkeleton = {
            ...user,
            skeletonMerge: skeletonMerge
          };
          console.log('🔍 AuthContext: Setting pendingUserData with skeletonMerge:', userWithSkeleton);
          setPendingUserData(userWithSkeleton);
        } else {
          console.log('ℹ️ AuthContext: Normal registration flow (no skeleton connections)');
          // Normal registration flow
          setPendingUserData(user);
        }

        setPendingToken(token);
        setShowAddressModal(true);
        setIsPendingRegistration(true);
        setIsLoading(false);
        return {
          success: true,
          user: user,
          token: token,
          skeletonMerge: skeletonMerge
        };
      } else {
        console.error('❌ AuthContext: Registration failed:', response.error);
        setIsLoading(false);
        return { success: false, error: response.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('❌ AuthContext: Registration exception:', error);
      setIsLoading(false);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed'
      };
    }
    // Note: Don't set isLoading(false) here for success case - will be done in completeRegistration
  };

  const completeRegistration = async (userData: any, token: string) => {
    console.log('🔍 AuthContext: completeRegistration called with userData:', userData);
    console.log('🔍 AuthContext: userData.skeletonMerge:', userData.skeletonMerge);

    // Set token in API service and storage
    apiService.setToken(token);
    await storage.setItem('authToken', token);
    await storage.setItem('userData', JSON.stringify(userData));

    console.log('✅ AuthContext: Setting user state with skeletonMerge data');
    setUser(userData);
    setIsPendingRegistration(false);
    setShowAddressModal(false);
    setPendingUserData(null);
    setPendingToken('');

    // Initialize push notifications after completing registration
    console.log('📱 Initializing push notifications after registration');
    await pushNotificationService.initialize();
    // Initialize local notifications for activity reminders
    await localNotificationService.initialize();
  };

  const logout = async () => {
    try {
      setIsLoading(true);

      // Unregister push notifications before logging out
      await pushNotificationService.unregister();

      const response = await apiService.logout();
      if (!response.success) {
        console.warn('Server logout failed:', response.error);
      }
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear user state even if logout fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await apiService.getProfile();
      if (response.success && response.data) {
        // Preserve skeletonMerge data from current user state if it exists
        const updatedUser = user?.skeletonMerge
          ? { ...response.data, skeletonMerge: user.skeletonMerge }
          : response.data;

        console.log('🔍 AuthContext: refreshUser preserving skeletonMerge:', user?.skeletonMerge);
        setUser(updatedUser);
        await storage.setItem('userData', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isPendingRegistration,
    showAddressModal,
    pendingUserData,
    pendingToken,
    login,
    register,
    completeRegistration,
    setShowAddressModal,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;